import React, {
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import SliderPattern from '@mlb/react-patterns/components/Slider/slider.tsx';
import {
    RailHotspot,
    Dot,
} from '@mlb/react-patterns/components/Slider/slider.style.ts';
import COLORS from '@mlb/react-patterns/style/universal.color';

import { playheadMode } from '../utils';

const Slider = styled(SliderPattern)`
    user-select: none;

    ${RailHotspot} {
        height: 30px;
        transform: translate(0%,-100%);
    }
`;

const Handle = styled(Dot)`
    width: 3px;
    height: 24px;
    top: -10px;
    background-color: ${COLORS.mlbWhite};
    border: none;
    border-radius: 0;
`;

export const KEYDOWN_STEP = 1;

const Playhead = (props) => {

    const {
        endTime,
        isIos,
        wasPlaying,
        isSliding,
        isTrimming,
        mediaPlayer,
        originalDuration,
        playhead,
        segments,
        setPlayhead,
        step,
        startTime,
    } = props;

    const mode = useCallback((curr, next) => {

        return playheadMode(curr, next, startTime, endTime);

    }, [
        startTime,
        endTime,
    ]);

    const keyDownHandler = useCallback((e) => {

        const validUpKeys = ['ArrowRight'];
        const validDownKeys = ['ArrowLeft'];

        const key = e.key || `${e.keyCode}`;

        if (!validUpKeys.concat(validDownKeys).includes(key)) {

            return false;

        }

        e.stopPropagation && e.stopPropagation();
        e.preventDefault && e.preventDefault();

        const increment = validUpKeys.includes(key) ? KEYDOWN_STEP : -KEYDOWN_STEP;
        const nextPlayhead = playhead + increment;

        if (nextPlayhead >= startTime && nextPlayhead < endTime) {

            if (wasPlaying) {

                mediaPlayer.seek(nextPlayhead);

            }

            setPlayhead(nextPlayhead);

        }

    }, [
        startTime,
        endTime,
        playhead,
        setPlayhead,
        wasPlaying,
        mediaPlayer,
    ]);

    return (
        <Slider
            defaultValues={[playhead]}
            minValue={0}
            maxValue={segments[segments.length - 1]}
            handlers={{
                onSlideStart: () => {

                    isSliding.current = true;

                    if (wasPlaying) {

                        mediaPlayer.pause();

                    }

                },
                onSlideEnd: (values) => {

                    isSliding.current = false;

                    setPlayhead(values[0]);

                    if (wasPlaying) {

                        mediaPlayer.play();

                    }

                },
                onUpdate: (values) => {

                    if (isSliding.current && !isTrimming.current) {

                        if ((!isIos || wasPlaying) && values[0] !== playhead) {

                            mediaPlayer.seek(values[0]);

                        } else {

                            setPlayhead(values[0]);

                        }

                    }

                },
            }}
            step={step}
            mode={mode}
            renderRail={({ getRailProps }) => {

                return (
                    <RailHotspot
                        {...getRailProps({
                            onMouseDown: () => isSliding.current = true,
                            onTouchStart: () => isSliding.current = true,
                        })}
                    />
                );

            }}
            renderHandles={({
                handles,
                getHandleProps,
                activeHandleID,
            }) => {

                const handle = handles[0];

                return (
                    <Handle
                        left={`${handle.percent}%`}
                        theme={'dark'}
                        role={'slider'}
                        aria-valuemin={0}
                        aria-valuemax={originalDuration}
                        aria-valuenow={handle.value}
                        isActive={activeHandleID === handle.id}
                        {...getHandleProps(handle.id)}
                        onKeyDown={(e) => keyDownHandler(e)}
                    />
                );

            }}
        />
    );

};

Playhead.propTypes = {
    endTime: PropTypes.number.isRequired,
    wasPlaying: PropTypes.bool.isRequired,
    isSliding: PropTypes.shape({
        current: PropTypes.bool.isRequired,
    }).isRequired,
    isTrimming: PropTypes.shape({
        current: PropTypes.bool.isRequired,
    }).isRequired,
    mediaPlayer: PropTypes.object.isRequired,
    originalDuration: PropTypes.number.isRequired,
    playhead: PropTypes.number.isRequired,
    segments: PropTypes.array.isRequired,
    setPlayhead: PropTypes.func.isRequired,
    step: PropTypes.number.isRequired,
    startTime: PropTypes.number.isRequired,
    isIos: PropTypes.bool,
};

Playhead.defaultProps = {
    isIos: false,
};

export default Playhead;
